import React from 'react';
import PropTypes from 'prop-types';

import settingsReader from '../../store/readHelpers/SettingsReader';
import navigationService from '../../services/NavigationService';

import styles from './footer.scss';
import sessionReader from '../../store/readHelpers/SessionReader';
import responseReader from '../../store/readHelpers/ResponseReader';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import branchRuleReader from '../../store/readHelpers/BranchRuleReader';
import uiService from '../../services/UIService';
import ProgressBarContainer from '../progress/ProgressBar';
import footerService from '../../services/FooterService';
import footerSelector from '../../selectors/footer/FooterSelector';
import MandatoryModal from '../modal/MandatoryModal';
import responseService from '../../services/ResponseService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import uiConstants from '../../constants/UIConstants';

class FooterNavButtons extends React.PureComponent {
    static propTypes = {
        currentlyShown: PropTypes.object.isRequired,
        settingValues: PropTypes.object,
        ui: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            showMandatoryModal: false,
            navigationDirection: null,
        };
    }

    hasAdditionalResources() {
        return (
            (settingsReader.hasLockdownAdditionalResources() || sessionReader.questionSetHasAdditionalResources())
            && !(sessionReader.userHasChromeKiosk() && !sessionReader.isInsidePwaChromeApp())
        );
    }

    showBackButton() {
        const { settingValues } = this.props;

        return (
            settingValues !== undefined
            && settingsReader.allowNavigateBack()
            && !sessionReader.shouldNavigateStrictlySequentially(true)
            && !branchRuleReader.hasBranchRules()
        );
    }

    isCurrentQuestionMandatoryAndUnanswered = () => {
        const { currentlyShown } = this.props;
        const question = assessmentReader.getQuestion(currentlyShown.questionId);

        if (question?.isMandatoryQuestion) {
            return !responseReader.isQuestionDone(question);
        }

        return false;
    };

    navigateWithMandatoryCheck = (direction) => {
        const { currentlyShown } = this.props;
        const question = assessmentReader.getQuestion(currentlyShown.questionId);

        const actions = {
            next: () => footerService.gotoNextTestStep(),
            previous: () => navigationService.navigateToPreviousStep(),
        };

        const action = actions[direction];
        if (!action) return;

        if (this.checkMarketPlaceForMandatoryModal() && question?.isMandatoryQuestion) {
            responseService.updateItemResult(currentlyShown.questionId);

            if (this.isCurrentQuestionMandatoryAndUnanswered()) {
                this.setState({ showMandatoryModal: true, navigationDirection: direction });
                return;
            }
        }

        action();
    };

    handleNextButtonClick = () => {
        this.navigateWithMandatoryCheck('next');
    };

    handlePreviousButtonClick = () => {
        this.navigateWithMandatoryCheck('previous');
    };

    handleReturnToQuestion = () => {
        this.setState({ showMandatoryModal: false, navigationDirection: null });
    };

    handleConfirmNext = () => {
        const { navigationDirection } = this.state;

        this.setState({ showMandatoryModal: false, navigationDirection: null }, () => {
            if (navigationDirection === 'next') {
                footerService.gotoNextTestStep();
            } else if (navigationDirection === 'previous') {
                navigationService.navigateToPreviousStep();
            }
        });
    };

    checkMarketPlaceForMandatoryModal = () => {
        return marketplacePropertiesService.isMandatoryQuestionEnabled();
    }

    render() {
        const { ui, currentlyShown, settingValues } = this.props;
        const { showMandatoryModal } = this.state;
        const modalFlag = showMandatoryModal && this.checkMarketPlaceForMandatoryModal();
        const { footer__navIcon: navIcon } = ui.variables;
        const isLargeText =
            ui.marketplaceTheme === 'ceq' &&
            ui.zoomFactor === uiConstants.ZOOM_FACTOR.LARGE &&
            ((assessmentReader.getSections() || []).length > 1);
        const largeText = isLargeText ? styles.largeText : '';
        const largeTextTestClass = isLargeText ? 'large-text' : '';
        const hiddenTrackClass = !settingsReader.showAssessmentTrack() || branchRuleReader.hasBranchRules() ? 'nav-buttons--withouttrack' : '';
        const additionalResourcesClass = this.hasAdditionalResources() ? 'nav-buttons--withadditionalresources' : '';
        const rubricClass = footerService.hasRubricToDisplay(currentlyShown.questionId) ? 'questionHasRubric' : '';
        const className = [
            styles.navButtons,
            hiddenTrackClass,
            additionalResourcesClass,
            largeText,
            largeTextTestClass,
            rubricClass,
        ].filter(Boolean).join(' ');
        const useArrowIcon = navIcon === 'arrow';
        const question = assessmentReader.getQuestion(currentlyShown.questionId);
        const isQuestionDone = !!question && responseReader.isQuestionDone(question);
        const isPromoteNextButtonOnDoneEnabled = settingsReader.isPromoteNextButtonOnDoneEnabled();
        const nextButtonClasses = [];

        const { prevAriaLabel, nextAriaLabel, navigationControlsLabel } = footerSelector.getNavigationButtonAriaLabels();

        if (!isQuestionDone && isPromoteNextButtonOnDoneEnabled) {
            nextButtonClasses.push(styles.demotedNextBtn);
        } else {
            nextButtonClasses.push(styles.promotedNextBtn);
        }

        if (isQuestionDone && isPromoteNextButtonOnDoneEnabled) {
            nextButtonClasses.push(styles.shakeButtonOnPageDone);
        }

        if (useArrowIcon) {
            nextButtonClasses.push(styles.arrowIconBtn);
        }

        // eslint-disable-next-line camelcase
        const isOneWay = !!settingValues?.enable_one_way_navigation;
        const landmarkRole = footerSelector.isA11yV5AndAboveSupported() ? undefined : 'navigation';
        return (
            <React.Fragment>
                <div
                    className={className}
                    role={landmarkRole}
                    aria-label={navigationControlsLabel}
                    id="footer-nav-wrapper"
                    tabIndex="-1"
                >
                    {settingsReader.isProgressBarQuestionsEnabled() && <ProgressBarContainer {...this.props} />}

                    {this.showBackButton() && (
                        <button
                            className={`${styles.previousBtn} ${useArrowIcon ? styles.arrowIconBtn : ''}`}
                            disabled={uiService.disabledPreviousButton()}
                            id="footer-nav-button-previous"
                            aria-label={prevAriaLabel}
                            onClick={this.handlePreviousButtonClick}
                        >
                            <i className={`fa fa-${navIcon}-left`} aria-hidden="true" />
                        </button>
                    )}

                    <button
                        className={nextButtonClasses.join(' ')}
                        disabled={uiService.disabledNextButton()}
                        id="footer-nav-button-next"
                        aria-label={nextAriaLabel}
                        onClick={this.handleNextButtonClick}
                    >
                        <i className={`fa fa-${navIcon}-right`} aria-hidden="true" />
                    </button>
                </div>

                <MandatoryModal
                    show={modalFlag}
                    isOneWay={isOneWay}
                    onReturnToQuestion={this.handleReturnToQuestion}
                    onConfirmNext={this.handleConfirmNext}
                />
            </React.Fragment>
        );
    }
}

export default FooterNavButtons;
